/**
 * 예약 주간 타임라인·현황 계산 (2026-08-20) — 순수 함수. React 밖에서 테스트한다.
 *
 * 시안(artifact `예약 화면 재설계`)의 세 화면이 필요로 하는 계산만 모았다:
 *   · 주간 타임라인 — 월요일 시작 7일 · 시간 격자 · 하루 안 이벤트 배치(겹치면 레인 분할)
 *   · 자원 카드의 "지금 상태" — 지금 가능 / 사용 중 · N시 해제 / 운영시간 밖
 *   · 나의 예약 시간축 묶음(오늘·이번 주·이후·지난)과 요약
 *   · 승인처리의 겹침 판정 · 대기 일수
 *
 * 기존 `reservation-calendar.ts`(월 그리드·슬롯·잔여) 는 그대로 두고 여기서 재사용한다 —
 * 월 뷰는 토글로 남으므로 그 계산은 계속 살아 있다.
 */
import type { ReservationDto, ReservationResourceDto } from '../../api/reservation';
import { buildDaySlots, dayKey, peakUsed, reservationsOfDay } from './reservation-calendar';

/** 1시간 = 44px — 시안(mk-hour 44px)과 같은 축척. CSS 와 이 값을 함께 바꾼다. */
export const HOUR_PX = 44;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface WeekDay {
  date: Date;
  isToday: boolean;
  isPast: boolean;
  isWeekend: boolean;
  /** 예약을 걸 수 없는 날(지난 날 · maxDaysAhead 초과) — 빗금으로 구분한다. */
  unavailable: boolean;
}

/** 그 날 0시(로컬). */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** 월요일 시작 주의 첫날 — 시안의 주 헤더(월~일)와 같은 순서. */
export function startOfWeek(d: Date): Date {
  const s = startOfDay(d);
  // getDay(): 일=0 … 토=6 → 월요일까지 되감는 일수.
  s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
  return s;
}

/**
 * 주간 헤더 7칸. `maxDaysAhead` 는 자원 규칙(서버 validateRequestedRange 와 같은 기준)이고,
 * 0 이하면 상한이 없는 것으로 본다.
 */
export function buildWeekDays(cursor: Date, today: Date, maxDaysAhead = 0): WeekDay[] {
  const first = startOfWeek(cursor);
  const todayKey = dayKey(today);
  const horizonKey = maxDaysAhead > 0
    ? dayKey(new Date(today.getTime() + maxDaysAhead * DAY_MS))
    : null;
  const out: WeekDay[] = [];
  for (let i = 0; i < 7; i += 1) {
    const date = new Date(first);
    date.setDate(first.getDate() + i);
    const key = dayKey(date);
    const isPast = key < todayKey;
    const tooFar = horizonKey !== null && key > horizonKey;
    out.push({
      date,
      isToday: key === todayKey,
      isPast,
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      unavailable: isPast || tooFar,
    });
  }
  return out;
}

export interface HourWindow {
  /** 격자 첫 시(정시로 내림). */
  startHour: number;
  /** 격자 끝 시(정시로 올림, 배타적). */
  endHour: number;
  /** 라벨로 찍을 시각들 — startHour … endHour-1. */
  hours: number[];
  /** 열 높이(px). */
  heightPx: number;
}

/** 운영시간을 정시 격자로 넓힌 창. 09:30~21:30 이면 09시~22시(13칸). */
export function buildHourWindow(resource: Pick<ReservationResourceDto, 'openMinute' | 'closeMinute'>): HourWindow {
  const startHour = Math.max(0, Math.floor(resource.openMinute / 60));
  const endHour = Math.min(24, Math.max(startHour + 1, Math.ceil(resource.closeMinute / 60)));
  const hours: number[] = [];
  for (let h = startHour; h < endHour; h += 1) hours.push(h);
  return { startHour, endHour, hours, heightPx: hours.length * HOUR_PX };
}

/** 자정 기준 분 → 격자 y(px). 창 밖이면 음수/초과가 나오므로 호출부가 자른다. */
export function minuteToY(minute: number, win: HourWindow): number {
  return ((minute - win.startHour * 60) / 60) * HOUR_PX;
}

/** 격자 y(px) → 자정 기준 분. */
export function yToMinute(y: number, win: HourWindow): number {
  return win.startHour * 60 + (y / HOUR_PX) * 60;
}

/** 분을 자원의 슬롯 격자(openMinute 기준 slotMinutes 배수)에 맞춘다 — 서버 NOT_ALIGNED 방지. */
export function snapToSlot(
  minute: number,
  resource: Pick<ReservationResourceDto, 'openMinute' | 'closeMinute' | 'slotMinutes'>,
  mode: 'floor' | 'ceil' | 'round' = 'round',
): number {
  const step = Math.max(1, resource.slotMinutes);
  const rel = (minute - resource.openMinute) / step;
  const k = mode === 'floor' ? Math.floor(rel) : mode === 'ceil' ? Math.ceil(rel) : Math.round(rel);
  const snapped = resource.openMinute + k * step;
  return Math.min(resource.closeMinute, Math.max(resource.openMinute, snapped));
}

export interface WeekEvent {
  id: string;
  start: Date;
  end: Date;
  /** 그 날 안에서만의 표시 구간(자정 넘김·창 밖은 잘린다). */
  topPx: number;
  heightPx: number;
  status: ReservationDto['status'];
  mine: boolean;
  requesterName: string;
  requesterId: string | null;
  requesterAvatarUrl: string | null;
  purpose: string | null;
  quantity: number;
  past: boolean;
  /** 겹치는 예약을 나란히 놓기 위한 레인 번호와 그 묶음의 레인 수. */
  lane: number;
  lanes: number;
}

/** 최소 표시 높이 — 15분짜리도 글자 한 줄은 보이게. */
const MIN_EVENT_PX = 18;

/**
 * 하루 열에 놓을 이벤트 배치. 겹치는 것끼리 묶어 레인을 나눈다(서로 가리지 않게).
 * 시안은 겹침 없는 회의실 기준이라 한 레인만 그리지만, 수량 2 이상 자원은 실제로 겹친다.
 */
export function layoutDayEvents(
  all: ReadonlyArray<ReservationDto>,
  day: Date,
  win: HourWindow,
  now: Date,
): WeekEvent[] {
  const dayStart = startOfDay(day).getTime();
  const rows = reservationsOfDay(all, day)
    .map((r) => {
      const s = new Date(r.startAt).getTime();
      const e = new Date(r.endAt).getTime();
      // 그 날 구간으로 자른 뒤 창(운영시간 정시 격자) 안으로 다시 자른다.
      const sMin = Math.max(win.startHour * 60, (Math.max(s, dayStart) - dayStart) / 60000);
      const eMin = Math.min(win.endHour * 60, (Math.min(e, dayStart + DAY_MS) - dayStart) / 60000);
      return { r, sMin, eMin };
    })
    .filter((x) => x.eMin > x.sMin)
    .sort((a, b) => a.sMin - b.sMin || a.eMin - b.eMin);

  // 겹침 묶음(cluster) 단위로 레인 수를 정해야 폭이 들쭉날쭉하지 않다.
  const out: WeekEvent[] = [];
  let cluster: typeof rows = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (cluster.length === 0) return;
    const laneEnds: number[] = [];
    const laneOf = new Map<number, number>();
    cluster.forEach((x, i) => {
      let lane = laneEnds.findIndex((endMin) => endMin <= x.sMin);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(x.eMin); } else { laneEnds[lane] = x.eMin; }
      laneOf.set(i, lane);
    });
    const lanes = Math.max(1, laneEnds.length);
    cluster.forEach((x, i) => {
      const topPx = minuteToY(x.sMin, win);
      const rawH = ((x.eMin - x.sMin) / 60) * HOUR_PX;
      out.push({
        id: x.r.id,
        start: new Date(x.r.startAt),
        end: new Date(x.r.endAt),
        topPx,
        heightPx: Math.max(MIN_EVENT_PX, rawH),
        status: x.r.status,
        mine: x.r.mine === true,
        requesterName: x.r.requester?.name ?? '',
        requesterId: x.r.requester?.id ?? null,
        requesterAvatarUrl: x.r.requester?.avatarUrl ?? null,
        purpose: x.r.purpose ?? null,
        quantity: x.r.quantity,
        past: new Date(x.r.endAt).getTime() <= now.getTime(),
        lane: laneOf.get(i) ?? 0,
        lanes,
      });
    });
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const x of rows) {
    if (cluster.length > 0 && x.sMin >= clusterEnd) flush();
    cluster.push(x);
    clusterEnd = Math.max(clusterEnd, x.eMin);
  }
  flush();
  return out;
}

/** 그 날 아직 잡을 수 있는 분(슬롯 중 잔여>0 인 것의 합). 헤더의 "N시간 빔" 이 이 값. */
export function freeMinutesOfDay(
  resource: ReservationResourceDto,
  day: Date,
  all: ReadonlyArray<ReservationDto>,
  now: Date,
): number {
  if (resource.bookingUnit === 'day') {
    const s = startOfDay(day);
    const e = new Date(s.getTime() + DAY_MS);
    return peakUsed(reservationsOfDay(all, day), s, e) >= resource.capacity ? 0 : 24 * 60;
  }
  const slots = buildDaySlots(resource, day, reservationsOfDay(all, day), now);
  return slots.reduce((sum, s) => (s.remaining > 0 && !s.past ? sum + resource.slotMinutes : sum), 0);
}

export type LiveKind = 'free' | 'busy' | 'out';

export interface LiveStatus {
  kind: LiveKind;
  /** busy 일 때 언제 풀리는지(모르면 null). */
  freeAt: Date | null;
  /** free 일 때 언제까지 연속으로 비는지(모르면 null). */
  freeUntil: Date | null;
  /** 오늘 남은 가용 분. */
  freeMinutes: number;
  /** 지금 잔여 수량. */
  remaining: number;
}

/**
 * 자원 카드/퀵칩의 "지금 상태".
 *   free — 지금 잡을 수 있다(언제까지 연속으로 비는지 함께)
 *   busy — 지금은 찼다(언제 풀리는지 함께)
 *   out  — 운영시간 밖이거나 오늘은 더 잡을 수 없다
 */
export function liveStatus(
  resource: ReservationResourceDto,
  all: ReadonlyArray<ReservationDto>,
  now: Date,
): LiveStatus {
  const today = reservationsOfDay(all, now);
  const freeMinutes = freeMinutesOfDay(resource, now, all, now);

  if (resource.bookingUnit === 'day') {
    const s = startOfDay(now);
    const e = new Date(s.getTime() + DAY_MS);
    const remaining = Math.max(0, resource.capacity - peakUsed(today, s, e));
    return {
      kind: remaining > 0 ? 'free' : 'busy',
      freeAt: null,
      freeUntil: null,
      freeMinutes,
      remaining,
    };
  }

  const slots = buildDaySlots(resource, now, today, now);
  const upcoming = slots.filter((s) => s.end.getTime() > now.getTime());
  if (upcoming.length === 0) {
    return { kind: 'out', freeAt: null, freeUntil: null, freeMinutes: 0, remaining: 0 };
  }
  const current = upcoming[0]!;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const beforeOpen = nowMin < resource.openMinute;

  if (current.remaining > 0 && !beforeOpen) {
    // 연속으로 비는 끝 — 처음으로 막히는 슬롯의 시작.
    let until = current.end;
    for (const s of upcoming) {
      if (s.remaining === 0) break;
      until = s.end;
    }
    return { kind: 'free', freeAt: null, freeUntil: until, freeMinutes, remaining: current.remaining };
  }

  if (beforeOpen) {
    return { kind: 'out', freeAt: current.start, freeUntil: null, freeMinutes, remaining: current.remaining };
  }

  const next = upcoming.find((s) => s.remaining > 0) ?? null;
  return { kind: 'busy', freeAt: next ? next.start : null, freeUntil: null, freeMinutes, remaining: 0 };
}

/** 두 예약의 시간이 겹치나 — 승인처리의 "먼저 볼 것" 판정. */
export function overlaps(a: ReservationDto, b: ReservationDto): boolean {
  const as = new Date(a.startAt).getTime();
  const ae = new Date(a.endAt).getTime();
  const bs = new Date(b.startAt).getTime();
  const be = new Date(b.endAt).getTime();
  return as < be && bs < ae;
}

/** 같은 자원 안에서 시간이 겹치는 대기 건들의 id 집합. */
export function conflictingIds(rows: ReadonlyArray<ReservationDto>): Set<string> {
  const hit = new Set<string>();
  for (let i = 0; i < rows.length; i += 1) {
    for (let j = i + 1; j < rows.length; j += 1) {
      const a = rows[i]!;
      const b = rows[j]!;
      if (a.resourceId !== b.resourceId) continue;
      if (!overlaps(a, b)) continue;
      hit.add(a.id);
      hit.add(b.id);
    }
  }
  return hit;
}

/** 신청 후 며칠째인가(달력일 차이). 0 이면 오늘 신청. */
export function waitingDays(createdAt: string, now: Date): number {
  const a = startOfDay(new Date(createdAt)).getTime();
  const b = startOfDay(now).getTime();
  return Math.max(0, Math.round((b - a) / DAY_MS));
}

export type MineBucket = 'today' | 'week' | 'later' | 'past';

/** 나의 예약 시간축 묶음 — 시안의 오늘 / 이번 주 / 지난. */
export function bucketOf(r: ReservationDto, now: Date): MineBucket {
  const start = new Date(r.startAt);
  const end = new Date(r.endAt);
  if (end.getTime() <= now.getTime()) return 'past';
  if (r.status === 'cancelled' || r.status === 'rejected') return 'past';
  if (dayKey(start) === dayKey(now)) return 'today';
  const weekEnd = new Date(startOfWeek(now).getTime() + 7 * DAY_MS);
  return start.getTime() < weekEnd.getTime() ? 'week' : 'later';
}

export interface MineSummary {
  /** 가장 가까운 다가오는 예약(취소·반려 제외). */
  next: ReservationDto | null;
  /** 다음 예약까지 남은 분(이미 시작했으면 0). */
  nextInMinutes: number;
  pendingCount: number;
  /** 가장 오래 기다린 대기 건의 일수. */
  pendingOldestDays: number;
  weekCount: number;
  weekMinutes: number;
}

export function summarizeMine(rows: ReadonlyArray<ReservationDto>, now: Date): MineSummary {
  const live = rows.filter((r) => r.status === 'pending' || r.status === 'approved');
  const upcoming = live
    .filter((r) => new Date(r.endAt).getTime() > now.getTime())
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  const next = upcoming[0] ?? null;
  const pending = rows.filter((r) => r.status === 'pending');
  const weekStart = startOfWeek(now).getTime();
  const weekEnd = weekStart + 7 * DAY_MS;
  const inWeek = live.filter((r) => {
    const s = new Date(r.startAt).getTime();
    return s >= weekStart && s < weekEnd;
  });
  return {
    next,
    nextInMinutes: next
      ? Math.max(0, Math.round((new Date(next.startAt).getTime() - now.getTime()) / 60000))
      : 0,
    pendingCount: pending.length,
    pendingOldestDays: pending.reduce((max, r) => Math.max(max, waitingDays(r.createdAt, now)), 0),
    weekCount: inWeek.length,
    weekMinutes: inWeek.reduce(
      (sum, r) => sum + Math.round((new Date(r.endAt).getTime() - new Date(r.startAt).getTime()) / 60000),
      0,
    ),
  };
}

export interface DurationParts {
  /** 종일 배수면 일수, 아니면 0. */
  days: number;
  hours: number;
  minutes: number;
}

/** 분을 일/시/분으로 쪼갠다 — 문구는 i18n(`tRsvDuration`)이 붙인다. */
export function splitDuration(total: number): DurationParts {
  if (total <= 0) return { days: 0, hours: 0, minutes: 0 };
  if (total >= 24 * 60 && total % (24 * 60) === 0) return { days: total / (24 * 60), hours: 0, minutes: 0 };
  return { days: 0, hours: Math.floor(total / 60), minutes: total % 60 };
}

/* ══════════════════════════════════════════════════════════════════════════
   월 뷰 · 일 단위 기간 막대 (2026-08-20 2차)
   시안: docs/reservation-views-sample.html
   ══════════════════════════════════════════════════════════════════════════ */

/** 월 그리드 6주 × 7일 — 주간 뷰와 같은 **월요일 시작** 축. */
export function buildMonthWeeks(cursor: Date, today: Date, maxDaysAhead = 0): WeekDay[][] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = startOfWeek(first);
  const weeks: WeekDay[][] = [];
  for (let w = 0; w < 6; w += 1) {
    const anchor = new Date(start);
    anchor.setDate(start.getDate() + w * 7);
    weeks.push(buildWeekDays(anchor, today, maxDaysAhead));
  }
  return weeks;
}

/** 그 날이 이번 달인가 — 월 그리드에서 흐리게 그릴지 판단. */
export function inMonth(day: Date, cursor: Date): boolean {
  return day.getFullYear() === cursor.getFullYear() && day.getMonth() === cursor.getMonth();
}

/** 시간 단위 — 그 날 잡을 수 있는 시간의 비율(0~1). 월 셀의 가용 막대. */
export function dayFreeRatio(
  resource: ReservationResourceDto,
  day: Date,
  all: ReadonlyArray<ReservationDto>,
  now: Date,
): number {
  const total = Math.max(1, resource.closeMinute - resource.openMinute);
  return Math.min(1, Math.max(0, freeMinutesOfDay(resource, day, all, now) / total));
}

/** 일 단위 — 그 날 남은 수량. 월/주 셀의 "1/2". */
export function dayRemainingCount(
  resource: ReservationResourceDto,
  day: Date,
  all: ReadonlyArray<ReservationDto>,
): number {
  const s = startOfDay(day);
  const e = new Date(s.getTime() + DAY_MS);
  return Math.max(0, resource.capacity - peakUsed(reservationsOfDay(all, day), s, e));
}

export interface SpanBar {
  id: string;
  /** 1-based 열(월=1 … 일=7) — CSS grid-column 에 그대로 넣는다. */
  startCol: number;
  span: number;
  lane: number;
  lanes: number;
  /** 이 주 이전부터 이어짐 / 다음 주로 이어짐 — 모서리를 각지게 해 잘림을 표시한다. */
  continuesLeft: boolean;
  continuesRight: boolean;
  start: Date;
  end: Date;
  status: ReservationDto['status'];
  mine: boolean;
  requesterName: string;
  purpose: string | null;
  quantity: number;
}

/**
 * 일 단위 예약을 한 주(7칸)에 가로 막대로 배치. 겹치면 레인을 나눈다.
 *
 * ⚠ 저장된 `endAt` 은 **반납 다음날 자정(배타)** 이다 — 마지막 칸은 `endAt - 1ms` 가 있는 날이다.
 *   그대로 쓰면 막대가 하루 더 길게 그려진다(fmtRange 가 같은 이유로 하루를 되돌린다).
 */
export function layoutSpanBars(
  all: ReadonlyArray<ReservationDto>,
  week: ReadonlyArray<WeekDay>,
): SpanBar[] {
  if (week.length === 0) return [];
  const weekStart = startOfDay(week[0]!.date).getTime();
  const weekEnd = startOfDay(week[week.length - 1]!.date).getTime() + DAY_MS;

  const rows = all
    .map((r) => {
      const s = new Date(r.startAt).getTime();
      // 배타적 끝 → 마지막으로 점유하는 날.
      const lastDay = startOfDay(new Date(new Date(r.endAt).getTime() - 1)).getTime();
      return { r, s: startOfDay(new Date(s)).getTime(), e: lastDay };
    })
    .filter((x) => x.e >= weekStart && x.s < weekEnd)
    .sort((a, b) => a.s - b.s || a.e - b.e);

  const laneEnds: number[] = [];
  const out: SpanBar[] = [];
  for (const x of rows) {
    const fromCol = Math.max(0, Math.round((x.s - weekStart) / DAY_MS));
    const toCol = Math.min(week.length - 1, Math.round((x.e - weekStart) / DAY_MS));
    if (toCol < fromCol) continue;
    let lane = laneEnds.findIndex((endCol) => endCol < fromCol);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(toCol); } else { laneEnds[lane] = toCol; }
    out.push({
      id: x.r.id,
      startCol: fromCol + 1,
      span: toCol - fromCol + 1,
      lane,
      lanes: 0,
      continuesLeft: x.s < weekStart,
      continuesRight: x.e >= weekEnd,
      start: new Date(x.r.startAt),
      end: new Date(x.r.endAt),
      status: x.r.status,
      mine: x.r.mine === true,
      requesterName: x.r.requester?.name ?? '',
      purpose: x.r.purpose ?? null,
      quantity: x.r.quantity,
    });
  }
  const lanes = Math.max(1, laneEnds.length);
  for (const b of out) b.lanes = lanes;
  return out;
}

/** 두 예약이 실제로 겹치는 구간 — 승인처리가 "15:00–16:00 이 겹칩니다" 로 쓴다. */
export function overlapWindow(a: ReservationDto, b: ReservationDto): { start: Date; end: Date } | null {
  const s = Math.max(new Date(a.startAt).getTime(), new Date(b.startAt).getTime());
  const e = Math.min(new Date(a.endAt).getTime(), new Date(b.endAt).getTime());
  return e > s ? { start: new Date(s), end: new Date(e) } : null;
}
